import { ALL_DATA_TYPES } from '@repo/advanced-filters';
import {
  Checkbox,
  Flex, InputNumber, Select, Typography,
} from 'antd';
import { waitUnitOptions } from 'automations/data/waitNode';
import { type WaitNodeData } from 'automations/types/nodeTypes';
import { useCallback, useEffect, useState } from 'react';

import MailMergeInput from './MailMergeInput';
import styles from './WaitNodeMenu.module.scss';

type ForDataType = Extract<WaitNodeData['additionalOptions']['wait'], { type: 'for' }>['for'];

export default function ForWaitContent({
  disabled,
  initialValue,
  onChange,
}: {
  disabled: boolean;
  initialValue: ForDataType;
  onChange: (value: ForDataType) => void;
}) {
  const [useMailMerge, setUseMailMerge] = useState(false);
  const [selectedValue, setSelectedValue] = useState(initialValue);

  const handleOffsetChange = useCallback((value: number | string | null) => {
    setSelectedValue({ offset: value ?? '', unit: selectedValue?.unit });
  }, [selectedValue]);

  const handleUnitChange = useCallback((value: ForDataType['unit']) => {
    setSelectedValue({ offset: selectedValue.offset, unit: value });
  }, [selectedValue]);

  useEffect(() => {
    if (selectedValue !== initialValue && !disabled) {
      onChange?.(selectedValue);
    }
  }, [onChange, selectedValue, initialValue, disabled]);

  return (
    <Flex gap={8} className={styles.forContainer}>
      <Typography.Text>
        For
      </Typography.Text>
      <Flex gap={16}>
        {useMailMerge ? (
          <MailMergeInput
            onChange={(value) => handleOffsetChange(value)}
            disabled={disabled}
            initialValue={initialValue.offset.toString()}
            allowedDataTypes={[ALL_DATA_TYPES.number]}
          />
        ) : (
          <InputNumber
            min={1}
            value={parseInt(selectedValue?.offset?.toString() ?? '1', 10)}
            onChange={handleOffsetChange}
            disabled={disabled}
          />
        )}
        <Select
          value={selectedValue?.unit}
          options={waitUnitOptions}
          onChange={handleUnitChange}
          className={styles.unitSelect}
          disabled={disabled}
        />
        {!disabled && (
          <Checkbox
            checked={useMailMerge}
            onChange={(e) => setUseMailMerge(e.target.checked)}
            className={styles.mailMergeCheckbox}
          >
            Use Mail Merge
          </Checkbox>
        )}
      </Flex>
    </Flex>
  );
}
